import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { getCustomerPolicy, publishCustomerPolicy } from "../../../services/customerPolicyApi";
import { formatDateTimeDMY12 } from "../../../utils/dateFormat";
import starterPoints from "./customerPolicyStarterPoints";

// The trip policy customers accept on the Trip Details link before the driver, vehicle, hotels and
// trip plan open. Publishing saves a new version; the first publish switches the acceptance on for
// every booking created from then on. Existing bookings are never affected.

// English only for now. Add ["te", "Telugu"], ["kn", "Kannada"], ["ta", "Tamil"], ["hi", "Hindi"]
// here (and their codes on the customer page) once the translations are approved; the server and
// the starter draft already carry them.
const LANGS = [
  ["en", "English *"],
];

const blankPoint = () => ({ en: "", te: "", kn: "", ta: "", hi: "", disabled: false });
// A disabled point stays here for later but is never shown to customers.
const toEditable = (points) => (points || []).map((point) => ({
  ...blankPoint(),
  ...Object.fromEntries(LANGS.map(([lang]) => [lang, point?.[lang] ?? ""])),
  disabled: Boolean(point?.disabled),
}));

export default function CustomerPolicyManager() {
  const [current, setCurrent] = useState(null);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [usingStarter, setUsingStarter] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCustomerPolicy();
      setCurrent(data);
      const live = toEditable(data?.points);
      setUsingStarter(!live.length);
      setPoints(live.length ? live : toEditable(starterPoints));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load the customer policy");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updatePoint = (index, lang, value) =>
    setPoints((old) => old.map((point, i) => (i === index ? { ...point, [lang]: value } : point)));
  const movePoint = (index, delta) =>
    setPoints((old) => {
      const target = index + delta;
      if (target < 0 || target >= old.length) return old;
      const next = [...old];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  const removePoint = (index) => setPoints((old) => old.filter((_, i) => i !== index));
  const toggleDisabled = (index) =>
    setPoints((old) => old.map((point, i) => (i === index ? { ...point, disabled: !point.disabled } : point)));
  const addPoint = () => setPoints((old) => [...old, blankPoint()]);

  const missingEnglish = points.findIndex(
    (point) => !String(point.en || "").trim() && LANGS.some(([lang]) => String(point[lang] || "").trim())
  );
  const activeCount = points.filter((point) => !point.disabled && String(point.en || "").trim()).length;

  const publish = async () => {
    if (missingEnglish >= 0) {
      toast.error(`Point ${missingEnglish + 1} needs the English text`);
      return;
    }
    if (!activeCount) {
      toast.error("Add at least one policy point that is not disabled");
      return;
    }
    const message = current
      ? "Publish these points as a new version? Customers who have not accepted yet will see the new text. Acceptances already given stay as they were."
      : "Publish the first version? From now on, every NEW booking's Trip Details link stays locked until the customer accepts these points with their name and location. Existing bookings are not affected.";
    if (!window.confirm(message)) return;
    setSaving(true);
    try {
      const saved = await publishCustomerPolicy(points);
      setCurrent(saved);
      setPoints(toEditable(saved.points));
      setUsingStarter(false);
      toast.success(`Version #${saved.id} published`);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not publish the policy");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="shadow-sm">
        <Card.Body className="text-center py-5"><Spinner /></Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm">
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <div className="fw-semibold">Customer Trip Policy</div>
          <div className="small text-muted">
            Customers accept these points on the Trip Details link, with their name and location, before the
            driver, vehicle, hotels and trip plan open. Applies to bookings created after the first publish.
          </div>
          <div className="small mt-1">
            {current ? (
              <Badge bg="success">
                Live: version #{current.id}, published {formatDateTimeDMY12(current.publishedAt, "-")}
              </Badge>
            ) : (
              <Badge bg="secondary">Not published - acceptance is off</Badge>
            )}
          </div>
        </div>
        <Button variant="primary" onClick={publish} disabled={saving}>
          {saving ? <><Spinner size="sm" className="me-2" />Publishing</> : current ? "Publish new version" : "Publish"}
        </Button>
      </Card.Header>
      <Card.Body>
        {usingStarter ? (
          <Alert variant="warning" className="small">
            These are draft starter points taken from the Travel Terms &amp; Conditions. Please get the wording
            approved before publishing. Nothing is live until you click Publish.
          </Alert>
        ) : null}
        <div className="small text-muted mb-3">
          Disable keeps a point here without showing it to customers; Remove deletes it. Changes go live only when you publish, and never
          change what earlier customers accepted.
        </div>

        <div className="vstack gap-3">
          {points.map((point, index) => (
            <Card key={index} className={`border ${point.disabled ? "customer-policy-point-disabled" : ""}`}>
              <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2 py-2">
                <span className="fw-semibold">
                  Point {index + 1}
                  {point.disabled ? <Badge bg="secondary" className="ms-2">Disabled - not shown to customers</Badge> : null}
                </span>
                <div className="d-flex flex-wrap gap-1">
                  <Button size="sm" variant="outline-secondary" disabled={saving || index === 0} onClick={() => movePoint(index, -1)}>
                    Up
                  </Button>
                  <Button size="sm" variant="outline-secondary" disabled={saving || index === points.length - 1} onClick={() => movePoint(index, 1)}>
                    Down
                  </Button>
                  <Button size="sm" variant={point.disabled ? "outline-success" : "outline-warning"} disabled={saving} onClick={() => toggleDisabled(index)}>
                    {point.disabled ? "Enable" : "Disable"}
                  </Button>
                  <Button size="sm" variant="outline-danger" disabled={saving} onClick={() => removePoint(index)}>
                    Remove
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <Row className="g-2">
                  {LANGS.map(([lang, label]) => (
                    <Col xs={12} md={lang === "en" ? 12 : 6} key={lang}>
                      <Form.Label className="small fw-semibold mb-1">{label}</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        maxLength={1000}
                        value={point[lang] || ""}
                        disabled={saving}
                        isInvalid={lang === "en" && index === missingEnglish}
                        onChange={(event) => updatePoint(index, lang, event.target.value)}
                      />
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          ))}
        </div>

        <Button variant="outline-primary" className="mt-3" onClick={addPoint} disabled={saving}>
          Add point
        </Button>
      </Card.Body>
    </Card>
  );
}
